import { useState, useEffect } from "react";
import "./Login.css";
import {
  signIn,
  signOut,
  resendSignUpCode,
  getCurrentUser,
  confirmSignIn,
} from "aws-amplify/auth";

import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useNavigate, useLocation } from "react-router-dom";
import { ForgotPassword } from "./Forgotpass";
import PasswordInputValidation from "../PasswordInputValidation/PasswordInputValidation";
import { SESSION_EXPIRED_KEY, safePostLoginPath } from "../../Utils/functions";

const postLoginDestination = () => {
  const redirectPath = new URLSearchParams(window.location.search).get("redirect");
  const decoded = redirectPath ? decodeURIComponent(redirectPath) : null;
  return safePostLoginPath(decoded || "/");
};

export const Login = ({ switchToSignup, setShowOtpInput, setEmail }) => {
  const [errorMessage, setErrorMessage] = useState("");
  const [sessionExpiredNotice, setSessionExpiredNotice] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showNewPasswordForm, setShowNewPasswordForm] = useState(false);
  const [usernameForPasswordChange, setUsernameForPasswordChange] = useState(
    ""
  );
  const [showNewPassword, setShowNewPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    try {
      if (sessionStorage.getItem(SESSION_EXPIRED_KEY) === "1") {
        setSessionExpiredNotice(true);
        sessionStorage.removeItem(SESSION_EXPIRED_KEY);
      }
    } catch (_) { /* ignore */ }

    const checkAuth = async () => {
      try {
        await getCurrentUser();
        navigate(postLoginDestination());
      } catch (error) {
        // User is not authenticated, stay on login page
        console.log("User is not authenticated");
      }
    };
    checkAuth();
  }, [location, navigate]);

  const finishSignIn = async (signInResult, username) => {
    if (signInResult.nextStep.signInStep === "CONFIRM_SIGN_UP") {
      setEmail(username);
      setShowOtpInput(true);
      await resendSignUpCode({ username });
    } else if (
      signInResult.nextStep.signInStep ===
      "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
    ) {
      setUsernameForPasswordChange(username);
      setShowNewPasswordForm(true);
    } else if (signInResult.nextStep.signInStep === "DONE") {
      window.location.href = postLoginDestination();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    const username = event.target.userEmail.value;
    const password = event.target.userPassword.value;
    try {
      let signInResult;
      try {
        signInResult = await signIn({ username, password });
      } catch (error) {
        if (error.name !== "UserAlreadyAuthenticatedException") throw error;
        try {
          const current = await getCurrentUser();
          const currentEmail = (
            current?.signInDetails?.loginId ||
            current?.username ||
            ""
          ).toLowerCase();
          if (currentEmail && currentEmail === username.toLowerCase()) {
            window.location.href = postLoginDestination();
            return;
          }
        } catch (_) {
          /* no readable current user; replace the session */
        }
        await signOut();
        signInResult = await signIn({ username, password });
      }
      await finishSignIn(signInResult, username);
    } catch (error) {
      console.error("Error signing in:", error);
      if (error.name === "NotAuthorizedException") {
        setErrorMessage("Incorrect username or password");
      } else if (error.name === "UserNotFoundException") {
        setErrorMessage("User does not exist");
      } else if (error.name === "UserNotConfirmedException") {
        setErrorMessage("User is not confirmed");
      } else {
        setErrorMessage("An error occurred during sign in. Please try again.");
      }
    }
  };

  const handleNewPasswordSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    const newPassword = event.target.newPassword.value;
    try {
      const confirmSignInResult = await confirmSignIn({
        challengeResponse: newPassword,
      });
      if (confirmSignInResult.nextStep.signInStep === "DONE") {
        window.location.href = postLoginDestination();
      }
    } catch (error) {
      console.error("Error confirming new password:", error);
      if (error.name === "NotAuthorizedException") {
        setErrorMessage("Password does not meet requirements or is invalid");
      } else {
        setErrorMessage(
          "An error occurred while setting your new password. Please try again."
        );
      }
    }
  };

  return (
    <div className="Login">
      <h2>Sign in</h2>
      {sessionExpiredNotice && (
        <div className="session-expired-message">
          Your session has expired. Please sign in again.
        </div>
      )}
      {errorMessage && <div className="error-message">{errorMessage}</div>}

      {showForgotPassword ? (
        <ForgotPassword switchToLogin={() => setShowForgotPassword(false)} />
      ) : showNewPasswordForm ? (
        <>
          <p>Please set a new password to continue.</p>
          <form onSubmit={handleNewPasswordSubmit}>
            <PasswordInputValidation
              name="newPassword"
              placeholder="New Password"
            />
            <button type="submit">SET NEW PASSWORD</button>
          </form>
          <p>
            <button
              onClick={() => {
                setShowNewPasswordForm(false);
                setUsernameForPasswordChange("");
                setErrorMessage("");
              }}
            >
              Back to Login
            </button>
          </p>
        </>
      ) : (
        <>
          <form onSubmit={handleSubmit}>
            <input name="userEmail" type="email" placeholder="Email" required />
            <div className="password-container">
              <input
                name="userPassword"
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </button>
            </div>
            <button type="submit">LOG IN</button>
          </form>
          <p>
            <button onClick={() => setShowForgotPassword(true)}>
              Forgot Password?
            </button>
          </p>
          <p>
            Don't have an account?{" "}
            <button onClick={switchToSignup}>SIGN UP</button>
          </p>
        </>
      )}
    </div>
  );
};
